import { createContext, useContext, useMemo, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { GraphQL } from '@guestgoodie/graphql'

import { useBranding } from '@/features/branding'
import { localesAsOptions } from '@/features/translations'
import Dropdown from '@/components/ui/Dropdown'
import { useExtractLocalizedErrors } from '../hooks/useExtractLocalizedErrors'

type ContextProps<T extends FieldValues = FieldValues> = {
  hasTranslations: boolean
  isTranslatable: (name: string) => boolean
  fields: (keyof T)[]
  locales: GraphQL.AppLocale[]
  defaultLocale: GraphQL.AppLocale
  nameToErroredLocale: Record<string, GraphQL.AppLocale[]> | null
}

type FieldContextProps = {
  locale: GraphQL.AppLocale
  setLocale: (locale: GraphQL.AppLocale) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Context = createContext<ContextProps<any> | null>(null)
export const FieldContext = createContext<FieldContextProps | null>(null)

type Props = {
  translatableFields: string[]
}

export function Translation(props: React.PropsWithChildren<Props>) {
  if (props.translatableFields.length === 0) return props.children

  return <TranslationProvider {...props} />
}

function TranslationProvider(props: React.PropsWithChildren<Props>) {
  const { children, translatableFields } = props

  const {
    branding: { defaultLocale, supportedLocales }
  } = useBranding()

  const locales = useMemo(
    () => Array.from(new Set([defaultLocale, ...(supportedLocales ?? [])])),
    [defaultLocale, supportedLocales]
  )

  const nameToErroredLocale = useExtractLocalizedErrors(defaultLocale)

  const value = useMemo(
    () => ({
      isTranslatable: (name: string) => {
        const sanitizedName = name
          .replace(/\.[0-9]+\./, '.[].')
          .replace(/\.[0-9]+$/, '.[]')

        return translatableFields.includes(sanitizedName)
      },
      fields: translatableFields,
      locales,
      defaultLocale,
      hasTranslations: translatableFields.length > 0,
      nameToErroredLocale
    }),
    [translatableFields, defaultLocale, locales, nameToErroredLocale]
  )

  if (!translatableFields || locales.length < 1) return children

  return <Context value={value}>{children}</Context>
}

export function FieldTranslation({ children }: React.PropsWithChildren) {
  const { defaultLocale } = useTranslation() ?? {}

  const [locale, setLocale] = useState(defaultLocale)

  const value = useMemo(() => {
    if (!locale) return null

    return { locale, setLocale }
  }, [locale, setLocale])

  if (!defaultLocale) return children

  return <FieldContext value={value}>{children}</FieldContext>
}

export function LocaleDropdown({ name }: { name: string }) {
  const { isTranslatable, hasTranslations, locales, nameToErroredLocale } =
    useTranslation() ?? {}

  const { locale, setLocale } = useFieldTranslation() ?? {}

  const erroredLocale = nameToErroredLocale?.[name]
  const hasTranslationError = locale ? erroredLocale?.includes(locale) : false

  const options = useMemo(() => {
    if (!locales) return []

    return localesAsOptions(locales)
  }, [locales])

  const showDropdown = useMemo(
    () => isTranslatable?.(name),
    [isTranslatable, name]
  )

  if (!hasTranslations || !showDropdown || !setLocale) return null

  return (
    <div>
      <Dropdown
        name={name}
        size="small"
        value={locale}
        options={options}
        onChange={setLocale}
        invalid={hasTranslationError}
      />
    </div>
  )
}

export function useTranslation<T extends FieldValues>() {
  const context = useContext(Context)

  if (!context) return null
  return context as ContextProps<T>
}

export function useFieldTranslation() {
  const context = useContext(FieldContext)

  if (!context) return null
  return context as FieldContextProps
}
